import React, { useEffect, useMemo, useState } from 'react';
import { Spinner, QuestionModal } from '../../../../../common/index.jsx';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useGetInternalJobQuery,
  useSaveInternalJobMutation,
  useDeleteInternalJobMutation,
} from '../../../../../../redux/services/fij_internal_jobs.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import FijJobForm from './FijJobForm.jsx';
import { useGetAreaOfUsesQuery } from '../../../../../../redux/services/fij_area_of_uses.jsx';
import { useGetStudyProgramsQuery } from '../../../../../../redux/services/study_programs.jsx';
import { useGetTeachersQuery } from '../../../../../../redux/services/users.jsx';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { DeleteFilled } from '@ant-design/icons';
import { HttpError } from '../../../../../common/HttpError/index.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import {
  ROLE_FRI_INTERNAL_JOBS_ADMIN,
  ROLE_FRI_INTERNAL_JOBS_LECTURER,
} from '../../../../../../utils/constants/roles.js';

const FijJobEdit = ({ mode, archiveMode }) => {
  const user = useSelector((state) => state.auth.user);
  const currentLecturerId =
    user?.person_id === null || user?.person_id === undefined
      ? user?.id === null || user?.id === undefined
        ? undefined
        : String(user.id)
      : String(user.person_id);
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isCreate = mode === 'create';
  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);
  const isFriInternalJobsAdmin = useUserHasRole(ROLE_FRI_INTERNAL_JOBS_ADMIN);
  const isFriInternalJobsLecturer = useUserHasRole(
    ROLE_FRI_INTERNAL_JOBS_LECTURER,
  );

  // Detect restore-from-archive flow when creating
  const restoreFromId = useMemo(() => {
    const qp = new URLSearchParams(location.search);
    const val = qp.get('restore_from');
    return isCreate && val ? val : null;
  }, [location.search, isCreate]);

  const { data: internalJobDetail, isLoading: isLoadingInternalJobDetail } =
    isCreate ? { data: null, isLoading: false } : useGetInternalJobQuery(id);

  // When restoring, fetch template of archived job to prefill create form
  const { data: restoreTemplate, isLoading: isLoadingRestoreTemplate } =
    restoreFromId
      ? useGetInternalJobQuery(restoreFromId)
      : { data: null, isLoading: false };

  const { data: areaOfUses } = useGetAreaOfUsesQuery();
  const { data: studyPrograms } = useGetStudyProgramsQuery();
  const { data: teachers } = useGetTeachersQuery({ onlyNames: true });
  const [saveInternalJob, { isSuccess: isSaveSuccess }] =
    useSaveInternalJobMutation();
  const [deleteInternalJob, { isSuccess: isDeleteSuccess }] =
    useDeleteInternalJobMutation();
  const { saveSuccess: toastSaveSuccess, deleteSuccess: toastDeleteSuccess } =
    useToastMessages();

  // Create pageTitle constant based on mode
  const pageTitle = isCreate
    ? restoreFromId
      ? t('Obnova FRI praxe z archívu')
      : t('Pridanie FRI praxe')
    : t('Úprava FRI praxe');

  // Set document title
  useEffect(() => {
    document.title = `${pageTitle} - FRI.prax`;
  }, [pageTitle]);

  useEffect(() => {
    if (isSaveSuccess) {
      toastSaveSuccess();
      navigate(-1);
    }
  }, [isSaveSuccess]);

  useEffect(() => {
    if (isDeleteSuccess) {
      toastDeleteSuccess();
      navigate(-1);
    }
  }, [isDeleteSuccess]);

  const initials = useMemo(() => {
    if (restoreFromId && restoreTemplate) {
      // Prefill from template but blank dates and change name
      return {
        ...restoreTemplate,
        date_from: undefined,
        date_to: undefined,
        // ensure supervisor defaults to current user if none
        supervisor_id:
          restoreTemplate.supervisor_id !== null &&
          restoreTemplate.supervisor_id !== undefined
            ? String(restoreTemplate.supervisor_id)
            : currentLecturerId,
      };
    }
    return {
      student_capacity: 1,
      supervisor_id: internalJobDetail
        ? String(internalJobDetail.supervisor_id)
        : currentLecturerId,
      ...internalJobDetail,
    };
  }, [restoreFromId, restoreTemplate, internalJobDetail, currentLecturerId, t]);

  const notAllowed =
    !isCreate &&
    isFriInternalJobsLecturer &&
    internalJobDetail?.supervisor_id !== user?.id &&
    !isFriInternalJobsAdmin;

  const archivedBlocked =
    !isCreate &&
    !archiveMode &&
    !isFriInternalJobsAdmin &&
    internalJobDetail?.archived_at !== null;

  if (
    (restoreFromId && isLoadingRestoreTemplate) ||
    (!isCreate && isLoadingInternalJobDetail)
  )
    return <Spinner />;
  if (notAllowed || archivedBlocked)
    return <HttpError showTitle={false} showShadow={false} statusCode={403} />;

  return (
    <>
      <AppBarCMS
        title={pageTitle}
        backUrl={-1}
        backUrlTooltip={t('Zoznam mojich FRI praxí')}
        secondaryActions={
          !isCreate && internalJobDetail
            ? [
                {
                  title: t('Vymazať'),
                  permission: 'fri-internal-jobs-jobs.destroy',
                  onClick: () => setRowToBeDeleted(internalJobDetail),
                  icon: <DeleteFilled />,
                },
              ]
            : []
        }
      />
      <div style={{ margin: '10px' }}>
        <FijJobForm
          areaOfUses={areaOfUses || []}
          archiveMode={archiveMode || !!restoreFromId}
          studyPrograms={studyPrograms || []}
          teachers={teachers}
          initialValues={initials}
          fijDetail={internalJobDetail}
          isCreate={isCreate}
          isTeacher={true}
          isFijAdmin={isFriInternalJobsAdmin}
          onSubmit={(values) => {
            const body = { ...values };
            // On create (including restore), create a new record
            saveInternalJob({
              body,
              id: archiveMode ? undefined : isCreate ? undefined : id,
            });
          }}
        />
      </div>

      <QuestionModal
        chosenPositive={() => deleteInternalJob(rowToBeDeleted.id)}
        question={t('Naozaj chcete vymazať túto položku?')}
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={t('Vymazať položku')}
      />
    </>
  );
};

FijJobEdit.propTypes = {
  mode: PropTypes.string,
  refetch: PropTypes.func,
  archiveMode: PropTypes.bool,
};

export default FijJobEdit;
